import React from "react";
import SchemaOutlinedIcon from "@mui/icons-material/SchemaOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useNavigate } from "react-router";
import { Badge } from "@/components/common/Badge";
import { Tooltip } from "@/components/common/Tooltip";
import { TooltipButton } from "@/components/common/TooltipButton";
import {
  isLockedTable,
  navigateSearchRow,
  openSearchRow,
  optionDomId,
  SearchRowItem,
} from "./searchRows";

const KIND_ICON: Record<SearchRowItem["kind"], React.ReactNode> = {
  model: <SchemaOutlinedIcon className="icon-xs text-fg-muted shrink-0" />,
  table: <TableChartOutlinedIcon className="icon-xs text-fg-muted shrink-0" />,
  team: <GroupsOutlinedIcon className="icon-xs text-fg-muted shrink-0" />,
  dataset: <LayersOutlinedIcon className="icon-xs text-fg-muted shrink-0" />,
};

interface SearchResultRowProps {
  item: SearchRowItem;
  isActive: boolean;
  isParentWashed: boolean;
  parentIsHit: boolean;
  onHover: () => void;
}

export const SearchResultRow: React.FC<SearchResultRowProps> = ({
  item,
  isActive,
  isParentWashed,
  parentIsHit,
  onHover,
}) => {
  const navigate = useNavigate();
  const locked = item.kind === "table" && isLockedTable(item.table);
  const openable = !locked;

  const name =
    item.kind === "model"
      ? item.model.name
      : item.kind === "table"
        ? item.table.name
        : item.kind === "team"
          ? item.team.name
          : item.dataset.name;
  const description =
    item.kind === "model"
      ? item.model.description
      : item.kind === "table"
        ? item.table.description
        : item.kind === "team"
          ? item.team.description
          : item.dataset.description;

  return (
    <div
      id={optionDomId(item)}
      role="option"
      aria-selected={isActive}
      onMouseEnter={onHover}
      onClick={() => navigateSearchRow(item, navigate)}
      className={`group flex flex-col gap-0.5 px-2 py-1 rounded-app ${
        openable ? "cursor-pointer" : "cursor-default"
      } ${isParentWashed && !isActive ? "bg-bg-hover" : ""} ${
        isActive ? "" : "hover:bg-bg-hover"
      }`}
      style={
        isActive
          ? {
              background: "var(--color-selected-light)",
              boxShadow: "inset 0 0 0 1px var(--color-accent)",
            }
          : undefined
      }
    >
      <div className="flex items-center gap-1 min-w-0">
        {KIND_ICON[item.kind]}
        <span
          className="text-fg truncate"
          style={{ fontWeight: "var(--font-weight-medium)", minWidth: 0 }}
        >
          {name}
        </span>
        {locked && (
          <Tooltip
            title="Data not viewable — shared for discovery only"
            deliberate
          >
            <span className="flex items-center shrink-0">
              <LockOutlinedIcon className="icon-xs text-fg-muted" />
            </span>
          </Tooltip>
        )}
        <span className="ml-auto flex items-center gap-1 shrink-0">
          {item.kind === "table" &&
            item.table.link_count !== undefined &&
            item.table.link_count > 0 && (
              <Badge
                label={`${item.table.link_count} ${item.table.link_count === 1 ? "link" : "links"}`}
                uppercase={false}
              />
            )}
          {item.kind === "team" && (
            <Badge
              label={`${item.team.member_count} ${item.team.member_count === 1 ? "member" : "members"}`}
              uppercase={false}
            />
          )}
          {item.kind === "dataset" && (
            <Badge
              label={`${item.dataset.member_count} ${item.dataset.member_count === 1 ? "table" : "tables"}`}
              uppercase={false}
            />
          )}
          {openable && (
            <span
              className={`transition-opacity ${
                isActive
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
              }`}
            >
              <TooltipButton
                title="Open in new tab"
                variant="default"
                size="xs"
                aria-label="Open in new tab"
                onClick={(e) => {
                  e.stopPropagation();
                  openSearchRow(item);
                }}
              >
                <OpenInNewIcon />
              </TooltipButton>
            </span>
          )}
        </span>
      </div>
      <div className="flex items-baseline gap-2 min-w-0 pl-4 text-fg-muted text-small">
        {item.kind === "model" && (
          <span className="shrink-0">{item.model.relation}</span>
        )}
        {item.kind === "dataset" && (
          <span className="shrink-0">
            {item.dataset.is_owner ? "Owner" : "Shared"}
          </span>
        )}
        {item.kind === "table" && (
          <span className="shrink-0">
            {item.table.is_computed ? (
              <span>Computed table</span>
            ) : isLockedTable(item.table) ? (
              <span className="italic">in (unavailable model)</span>
            ) : (
              <span>
                in{" "}
                {parentIsHit ? (
                  <span className="text-accent">
                    <ArrowUpwardIcon
                      sx={{ fontSize: "var(--font-size-x-small)" }}
                    />
                    {item.table.parent_model.name}
                  </span>
                ) : (
                  <span>{item.table.parent_model.name}</span>
                )}
              </span>
            )}
          </span>
        )}
        <span className="truncate" style={{ minWidth: 0 }}>
          {description ?? " "}
        </span>
      </div>
    </div>
  );
};
